import dayjs from "dayjs";
import { EntryLogTable } from "features/log/components/table/entry-log";
import {
  EntryLogFilterFormValues,
  EntryLogFilterFormValuesPaginated,
  VehicleFilterFormValuesPaginated,
} from "features/log/components/Top-bar/type";
import { VehicleTopBar } from "features/log/components/Top-bar/vehicle-top-bar";
import { useGetLogList } from "features/log/use-cases/get-getSuppliers";
import { omit } from "lodash";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "utils/date";

const PAGE_SIZE_KEY = "entry_log_page_size";

export const LogView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userEditData, setUserEditData] = useState<any>(undefined);
  const [showAppUserModal, setShowAppUserModal] = useState(false);

  // -------------------- URL → params --------------------
  const params: any = useMemo(() => {
    const obj = Object.fromEntries(searchParams.entries());

    const entryDates = searchParams.getAll("date");
    const exitDates = searchParams.getAll("date1");

    return {
      page: Number(obj.page) || 1,
      size:
        Number(obj.size) ||
        Number(localStorage.getItem(PAGE_SIZE_KEY)) ||
        10,

      DriverName: obj.DriverName || undefined,
      SupplierName: obj.SupplierName || undefined,
      VehicleNumber: obj.VehicleNumber || undefined,
      EntryTypeId: obj.EntryTypeId ? Number(obj.EntryTypeId) : undefined,
      StatusId: obj.StatusId ? Number(obj.StatusId) : undefined,

      date: entryDates.length ? entryDates : undefined,
      date1: exitDates.length ? exitDates : undefined,
    };
  }, [searchParams]);

  // -------------------- Prepare API payload --------------------
  const preparePayload = (p: EntryLogFilterFormValuesPaginated) => {
    const entryStart = p.date?.[0];
    const entryEnd = p.date?.[1];
    const exitStart = p.date1?.[0];
    const exitEnd = p.date1?.[1];

    return {
      ...omit(p, ["date", "date1"]),
      EntryStartDate:
        entryStart && dayjs(entryStart).isValid()
          ? formatDate({
            date: dayjs(entryStart).startOf("day"),
            format: "dbDate",
          })
          : undefined,

      EntryEndDate:
        entryEnd && dayjs(entryEnd).isValid()
          ? formatDate({
            date: dayjs(entryEnd).endOf("day"),
            format: "dbDate",
          })
          : undefined,

      ExitStartDate:
        exitStart && dayjs(exitStart).isValid()
          ? formatDate({
            date: dayjs(exitStart).startOf("day"),
            format: "dbDate",
          })
          : undefined,

      ExitEndDate:
        exitEnd && dayjs(exitEnd).isValid()
          ? formatDate({
            date: dayjs(exitEnd).endOf("day"),
            format: "dbDate",
          })
          : undefined,
    };
  };

  const payload = useMemo(() => preparePayload(params), [params]);

  // -------------------- API call --------------------
  const getvehcListQueryResponse: any = useGetLogList(payload, {
    retry: false,
  });

  // -------------------- Handle filters --------------------
  const handleFiltersChange = (values: VehicleFilterFormValuesPaginated) => {
    const filtered = Object.entries(values).reduce<Record<string, any>>(
      (acc, [key, value]) => {
        if (value !== undefined && value !== "") {
          acc[key] = value;
        }
        
return acc;
      },
      {}
    );
    const search: Record<string, string | string[]> = {
      ...filtered,
      page: "1",
      size: params.size.toString(),
    };

    // Entry Date range
    if (values.date?.length) {
      search.date = values.date.map((d: any) => d.toISOString());
    }

    // Exit Date range
    if (values.date1?.length) {
      search.date1 = values.date1.map((d: any) => d.toISOString());
    }

    setSearchParams(search);
  };

  // -------------------- Handle pagination --------------------
  const handleTableChange = (newParams: { page: number; size: number }) => {
    localStorage.setItem(PAGE_SIZE_KEY, newParams.size.toString());

    const currentFilters = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== "")
    );

    setSearchParams({
      ...currentFilters,
      page: newParams.page.toString(),
      size: newParams.size.toString(),
    });
  };

  // -------------------- Initial form values --------------------
  const getInitialFilterValues: EntryLogFilterFormValues = {
    DriverName: params.DriverName,
    SupplierName: params.SupplierName,
    VehicleNumber: params.VehicleNumber,
    EntryTypeId: params.EntryTypeId,
    StatusId: params.StatusId,
    date: params.date,
    date1: params.date1,
  };

  return (
    <>
      <VehicleTopBar
        getInitialFilterValues={getInitialFilterValues}
        handleFilter={handleFiltersChange}
        userEditData={userEditData}
        showAppUserModal={showAppUserModal}
        setShowAppUserModal={setShowAppUserModal}
        onRefresh={getvehcListQueryResponse.refetch}
      />

      <EntryLogTable
        isvehicleListLoading={getvehcListQueryResponse.isFetching}
        vehicleList={getvehcListQueryResponse?.data?.res?.items}
        itemsCount={getvehcListQueryResponse.data?.totalRecords || 0}
        tableParams={params as any}
        updateParams={handleTableChange as any}
        setUserEditData={setUserEditData}
        setShowAppUserModal={setShowAppUserModal}
        onRefresh={getvehcListQueryResponse.refetch}
        isFetching={getvehcListQueryResponse.isFetching}
      />
    </>
  );
};
